<template>
  <div class="ma-deviation page-shell">
    <!-- ============ 工具栏 row 1 ============ -->
    <div class="toolbar">
      <span class="toolbar-label">样本池</span>
      <el-select v-model="sample" size="small" style="width: 158px" @change="onFilterChange">
        <el-option
          v-for="opt in poolOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        />
      </el-select>

      <span class="toolbar-label">均线</span>
      <el-select v-model="win" size="small" style="width: 148px" @change="onWindowChange">
        <el-option
          v-for="w in windows"
          :key="w"
          :label="winShort[w]"
          :value="w"
        />
      </el-select>

      <span class="toolbar-label">排序</span>
      <el-radio-group v-model="sort" size="small" @change="onFilterChange">
        <el-radio-button label="dev_asc">偏离最低</el-radio-button>
        <el-radio-button label="dev_desc">偏离最高</el-radio-button>
      </el-radio-group>

      <span class="spacer" />
      <el-popover
        placement="bottom-end"
        width="480"
        trigger="click"
        popper-class="mad-notice-popper"
      >
        <div class="mad-notice-pop">
          <div class="mad-notice-title">先说结论（研究报告 {{ reportDate }}，口径 {{ backtestCaliber || '—' }}）</div>
          <p>
            把「股价回到中长线均线附近」当成买点，<b>实测没有效果</b>。
            甜蜜区 <b>−3% ~ +3%</b> 的未来 20 日相对池等权超额，在三个配置下分别是
            <b>{{ backtest.sweet.map((s) => pct(s.excess)).join(' / ') }}</b>
            —— <b>全部约等于 0，且全部低于双边成本 0.08%</b>。
          </p>
          <p>
            <b>真正有结构的是「偏离越负越好」</b>（单调，不是非单调）：深度跌破（≤ −35%）档实测超额
            <span class="up">{{ pct(backtest.excess['250h20'][1]) }}</span>（年线，5/5 年为正）；
            反过来「严重追高」（&gt; +50%）三个配置一致
            <span class="down">{{ pct(backtest.excess['250h20'][12]) }}</span>，是最稳的<b>避雷</b>信号。
          </p>
          <p>
            ⚠️ 这层结构<b>与库内已有的反转因子 <code>rev60</code> 同源</b>（<code>all_rev60_h10</code> 年化 +15.07%，全库第 1）
            —— 若真要赚这个钱，<b>用 <code>rev60</code> 而不是「距均线偏离」</b>。
          </p>
          <p v-if="!excessMeasured" class="imp">
            本页当前选的是季线 MA60，该窗口未做回测，故「实测超额」列显示「未测」。
          </p>
        </div>
        <button slot="reference" type="button" class="notice-trigger" aria-label="研究结论说明">
          <i class="el-icon-warning-outline" />
          <span>说明</span>
        </button>
      </el-popover>
      <button type="button" class="doc-trigger" aria-label="分档超额" @click="dlgBacktest = true">
        <i class="el-icon-data-analysis" />
        <span>分档超额</span>
      </button>
      <button type="button" class="doc-trigger" aria-label="口径说明" @click="dlgCaliber = true">
        <i class="el-icon-info-outline" />
        <span>口径</span>
      </button>
      <el-button
        size="small"
        icon="el-icon-download"
        :loading="exporting"
        @click="exportAll"
      >
        导出
      </el-button>
      <el-button
        type="primary"
        size="small"
        icon="el-icon-refresh"
        :loading="loading"
        @click="loadData"
      >
        刷新
      </el-button>
    </div>

    <!-- ============ 工具栏 row 2：分档筛选 ============ -->
    <div class="toolbar toolbar--sub">
      <span class="toolbar-label">偏离分档</span>
      <span class="fchips">
        <span
          class="fchip"
          :class="{ on: bucket === 'all' }"
          @click="setBucket('all')"
        >
          全部<span class="c">{{ stats.count }}</span>
        </span>
        <span
          v-for="g in groups"
          :key="g.key"
          class="fchip"
          :class="{ on: bucket === g.key }"
          @click="setBucket(g.key)"
        >
          {{ g.label }}<span class="c">{{ stats.groups[g.key] || 0 }}</span>
        </span>
      </span>
      <span class="spacer" />
      <el-checkbox v-model="includeSt" size="small" @change="onFilterChange">
        含 ST / 退市股
      </el-checkbox>
    </div>

    <!-- ============ 就绪 / 新鲜度 / 错误 ============ -->
    <el-alert
      v-if="!ready && readyReason"
      :title="readyReason"
      type="warning"
      show-icon
      :closable="false"
      style="margin: 0 0 12px; text-align: left"
    />

    <el-alert
      v-if="ready && !maFresh"
      :title="freshHint"
      type="error"
      show-icon
      :closable="false"
      style="margin: 0 0 12px; text-align: left"
    />

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      style="margin: 0 0 12px; text-align: left"
    />

    <!-- ============ 汇总（默认折叠） ============ -->
    <el-collapse v-model="summaryOpen" class="summary-collapse">
      <el-collapse-item title="数据概览" name="summary">
        <div class="cards">
          <div class="card">
            <div class="l">当前样本池股票数</div>
            <div class="v">{{ stats.count }}</div>
            <div class="s">
              {{ sampleLabel }} · 已剔除 ST/退市 <b>{{ stats.stCount }}</b> 只 · 均线基准 {{ maAsOf || '—' }}
            </div>
          </div>
          <div class="card">
            <div class="l">偏离率中位数（{{ winShort[win] }}）</div>
            <div class="v" :class="clsOf(stats.medianDev)">{{ pct(stats.medianDev) }}</div>
            <div class="s">均值 {{ pct(stats.meanDev) }} · 负值 = 在均线下方</div>
          </div>
          <div class="card">
            <div class="l">「均线附近」家数（±15%）</div>
            <div class="v">{{ stats.nearCount }}</div>
            <div class="s">
              占池 {{ sharePct(stats.nearShare) }} · 其中「甜蜜区 −3%~+3%」实测超额
              <b>{{ excessMeasured ? pct(excessOf(bucketIdxOf('near'))) : '未测' }}</b>
              <template v-if="excessMeasured"> ⇒ 原假设已证伪</template>
            </div>
          </div>
          <div class="card warn">
            <div class="l">「严重追高」家数（&gt; +50%）</div>
            <div class="v down">{{ stats.chaseCount }}</div>
            <div class="s">
              该档实测超额 <b>{{ excessMeasured ? pct(excessOf(12)) : '未测' }}</b>
              <template v-if="excessMeasured"> · 分年 {{ backtest.stableYears[12] }}/5 为正 ⇒ 避雷列</template>
              <template v-else> · MA60 未做回测，不做避雷断言</template>
            </div>
          </div>
        </div>

        <!-- 分布图：跟汇总一起折叠；展开时再 resize，避免折叠态宽高为 0 -->
        <div class="block block--in-summary">
          <div class="block-head">
            <h3>偏离率分布 × 各档实测超额</h3>
            <span class="hint">{{ sampleLabel }} · {{ stats.count }} 只 · 均线基准 {{ maAsOf || '—' }} · 分档按 {{ winShort[win] }}</span>
            <span class="spacer" style="flex:1" />
            <span class="hint">现价来源：{{ priceSrcSummary }}</span>
          </div>
          <div class="legend">
            <span><span class="swatch swatch-bar" />当前池落在该档的家数占比（左轴）</span>
            <template v-if="excessMeasured">
              <span><span class="swatch swatch-up" />该档实测超额 · 正（右轴）</span>
              <span><span class="swatch swatch-down" />该档实测超额 · 负（右轴）</span>
              <span class="dim">实测值来自全市场回测（已剔除退市股）· 持有 20 日</span>
            </template>
            <span v-else class="dim">季线 MA60 未做回测 ⇒ 仅显示家数占比，不画超额线（不引用其它窗口的数字）</span>
          </div>
          <div ref="distChart" class="dist-chart" />
        </div>
      </el-collapse-item>
    </el-collapse>

    <!-- ============ 样本列表 ============ -->
    <div class="block">
      <div class="block-head">
        <h3>样本列表</h3>
        <span class="hint">
          共 {{ total }} 只 · 池 = {{ sampleLabel }}{{ includeSt ? '（含 ST）' : '（已剔除 ST）' }}
          <template v-if="bucket !== 'all'"> · 分档 = {{ groupLabel(bucket) }}</template>
        </span>
        <span class="spacer" style="flex:1" />
        <span class="hint">排序：偏离率（{{ winShort[win] }}）</span>
      </div>

      <el-table
        ref="tbl"
        v-loading="loading"
        :data="rows"
        :height="tableHeight"
        size="mini"
        stripe
        style="width: 100%"
        :row-class-name="rowClass"
      >
        <el-table-column type="index" label="#" width="52" align="right" :index="rowIndex" />
        <el-table-column label="代码" width="88">
          <template slot-scope="{ row }">
            <el-link :href="stockstarCodeLink(row.code)" target="_blank" rel="noopener noreferrer">
              {{ row.code }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column label="名称" width="126" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <el-link :href="eastmoneyCodeLink(row)" target="_blank" rel="noopener noreferrer">
              {{ row.name }}
            </el-link>
            <span v-if="row.isSt" class="tag st">ST</span>
          </template>
        </el-table-column>
        <el-table-column label="行业" width="112" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span class="dim">{{ row.industry || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="现价" width="82" align="right">
          <template slot-scope="{ row }">
            <span class="num">{{ num(row.price) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          v-for="w in windows"
          :key="'ma' + w"
          :label="winShort[w]"
          width="100"
          align="right"
          :class-name="w === win ? 'col-active' : ''"
        >
          <template slot="header">
            {{ winShort[w] }}
            <span class="ghost">MA{{ w }}</span>
          </template>
          <template slot-scope="{ row }">
            <span class="num dim">{{ num(row['ma' + w]) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          v-for="w in windows"
          :key="'dev' + w"
          :label="'偏离' + w"
          width="92"
          align="right"
          :class-name="w === win ? 'col-active' : ''"
        >
          <template slot-scope="{ row }">
            <span :class="row['dev' + w] == null ? 'dim' : 'num'">
              {{ row['dev' + w] == null ? '—' : pct(row['dev' + w]) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="分档" width="94">
          <template slot-scope="{ row }">
            <span class="tag" :class="row.bucket">{{ row.bucketLabel }}</span>
          </template>
        </el-table-column>
        <el-table-column width="152" align="right">
          <template slot="header">
            该档实测超额
            <el-tooltip :content="excessNote" placement="top">
              <i class="el-icon-question tip" />
            </el-tooltip>
          </template>
          <template slot-scope="{ row }">
            <template v-if="row.excess !== null && row.excess !== undefined">
              <span :class="clsOf(row.excess)">{{ pct(row.excess) }}</span>
              <span v-if="row.stableYears >= 4" class="tag yrs">{{ row.stableYears }}/5年正</span>
            </template>
            <span v-else class="ghost">未测</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="62" align="center">
          <template slot-scope="{ row }">
            <el-link :href="stockstarCodeLink(row.code)" target="_blank" rel="noopener noreferrer">K线</el-link>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="!loading && !rows.length && ready" class="empty-tip">
        当前条件下没有样本{{ bucket !== 'all' ? '（选中的分档在该均线窗口下为空 —— 例如 MA60 极少出现 ≤ −35% 的深度跌破）' : '' }}。
      </div>

      <el-pagination
        v-if="total > pageSize"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        layout="prev, pager, next, jumper, total"
        style="margin-top: 10px; text-align: right"
        @current-change="loadData"
      />

      <div class="legend legend--tags">
        <span v-for="g in groups" :key="g.key"><span class="tag" :class="g.key">{{ g.label }}</span>{{ groupRange(g.key) }}</span>
        <span class="dim">偏离 = 现价 ÷ 均线 − 1（均线用<b>后复权</b>算，再按最新收盘折算回真实价）</span>
        <span><span class="up">+x%</span> / <span class="down">−x%</span> 仅用于「该档实测超额」列：红 = 正超额</span>
      </div>
    </div>

    <!-- ============ 分档超额弹窗 ============ -->
    <el-dialog
      title="分档口径与实测超额（三个已回测配置）"
      :visible.sync="dlgBacktest"
      width="720px"
      append-to-body
      custom-class="mad-doc-dialog"
    >
      <div class="fnote">
        「实测超额」= 该档股票未来 N 日的<b>相对当期池等权</b>收益（已去掉市场 beta），单位 %。
        桶间比较不受成本影响，但<b>能否交易</b>要看某档超额是否 &gt; 成本 <code>0.08%</code>。
        四配置里最像「有 alpha」的深度跌破档，年化仍不过 5% 门槛。
      </div>
      <div class="table-scroll">
        <table class="grid">
          <thead>
            <tr>
              <th>偏离档（年线）</th>
              <th class="num">MA250<br><span class="ghost">持20日</span></th>
              <th class="num">MA120<br><span class="ghost">持20日</span></th>
              <th class="num">MA250<br><span class="ghost">持60日</span></th>
              <th class="num">分年</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(lb, i) in bucketLabels"
              :key="lb"
              :class="{ 'row-hot': i === 6, 'row-deep': i <= 1 }"
            >
              <td>
                {{ lb }}
                <span v-if="i === 6" class="tag near">甜蜜区</span>
              </td>
              <td class="num" :class="clsOf(backtest.excess['250h20'][i])">{{ pct(backtest.excess['250h20'][i]) }}</td>
              <td class="num" :class="clsOf(backtest.excess['120h20'][i])">{{ pct(backtest.excess['120h20'][i]) }}</td>
              <td class="num" :class="clsOf(backtest.excess['250h60'][i])">{{ pct(backtest.excess['250h60'][i]) }}</td>
              <td class="num">
                <span v-if="backtest.stableYears[i] >= 4" class="tag yrs">{{ backtest.stableYears[i] }}/5</span>
                <span v-else class="ghost">{{ backtest.stableYears[i] }}/5</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="fnote dim" style="margin-top: 10px">
        ⇒ 只有「深度跌破」一档显著为正且跨年稳定；「均线附近」是全部档位里最没信息的一档。
        ⚠️ 尾部彩票警示：<code>−∞~−50%</code> 桶样本极少且分年不稳（MA250 持 60 日的 2025 为 −8.83%，MA120 持 20 日甚至 −6.67%）
        —— 高超额 + 少样本 = 先当彩票，不当信号。
      </div>
    </el-dialog>

    <!-- ============ 口径弹窗 ============ -->
    <el-dialog
      title="口径、局限与两条更新链路"
      :visible.sync="dlgCaliber"
      width="640px"
      append-to-body
      custom-class="mad-doc-dialog"
    >
      <dl class="kv">
        <dt>偏离定义</dt>
        <dd><code>dev = 现价 ÷ MA_N − 1</code>；MA_N = 最近 N 个<b>已收盘</b>交易日的（后复权）收盘均值</dd>

        <dt>⚠️ 复权口径</dt>
        <dd class="imp">
          均线必须用后复权价算（不复权会被除权跳空打断）。展示时须把均线<b>按最新收盘折算回真实价</b>：
          <code>MA_real = MA_hfq × k</code>，<code>k = 真实收盘 ÷ 后复权收盘</code>。
          实测该比值 83 个交易日内漂移 2.4%~5.6% ⇒ <b>不能省</b>，省掉会把茅台算成 −89%（真值 −5.8%）
        </dd>

        <dt>⚠️ 两条更新链路</dt>
        <dd>
          <b>① 均线 MA60/120/250：每交易日 1 次</b> —— 线上没有 <code>market_daily_hfq</code>（且总内存仅 1.9G），
          只能<b>本地算 → 落 <code>ma_dev_base</code> → dump 推线上</b>（<code>scripts/build-ma-dev.js</code> + <code>deploy/sync-ma-dev.sh</code>）<br>
          <b>② 偏离率 = 现价 ÷ 均线 − 1：每分钟（跟随报价源）</b> —— <b>请求时现算</b>（join <code>realtime_quote</code>，一次除法）。
          <b>刻意不落表</b>：把实时值写死反而会过期
        </dd>

        <dt>⚠️ 均线是日频常数</dt>
        <dd>
          MA_N 一天只变一次，盘中变的只有现价 ⇒ 刷新频率跟随<b>报价源（当前 1 分钟）</b>即可，
          不需要更高频。当前现价来源：<b>{{ priceSrcSummary }}</b>
        </dd>

        <dt>⚠️ 深跌档含 ST</dt>
        <dd>
          全市场池的「深度跌破」档有 <b>31% 是 ST / 退市股</b>（实测 144 只中 45 只）；
          机构池（Top500 / P60 / P75）<b>ST 为 0</b>。故对比度分析请优先看机构池，或用上方的「含 ST」开关对照
        </dd>

        <dt>分档边界</dt>
        <dd>左开右闭 13 档，围绕 0 加密：<code>−∞/−50/−35/−25/−15/−8/−3/+3/+8/+15/+25/+35/+50/+∞</code>（%）</dd>

        <dt>未覆盖</dt>
        <dd>
          次新股（上市不足 N 个自身交易日）无值，<b>返回「—」而不是 0</b>（避免幻影值混进横截面）；
          停牌股（基准日当天无自身行情）已被剔除；北交所三源不供数
        </dd>

        <dt>样本口径</dt>
        <dd>
          与【波段雷达】同源：<code>top500</code> 来自 Top500 池文件，<code>p60/p75</code> 来自系统推荐的市值分位阈值
        </dd>

        <dt>研究报告</dt>
        <dd><a :href="reportHref" target="_blank" rel="noopener noreferrer">{{ backtest.report }}</a></dd>
      </dl>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { fetchWithCredentials } from '../utils/http.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'
import * as XLSX from 'xlsx'

echarts.use([BarChart, LineChart, GridComponent, TooltipComponent, CanvasRenderer])

// 红涨绿跌（A 股习惯）；本页只用于「实测超额」与「偏离率」两组数值（中性色用于价格）
const RED = '#F56C6C'
const GREEN = '#67C23A'

const WIN_SHORT = { 60: '季线 MA60', 120: '半年线 MA120', 250: '年线 MA250' }
const POOL_LABELS = { all: '全市场', top500: 'Top500', p75: 'P75严格', p60: 'P60宽松' }

export default {
  name: 'MaDeviation',
  props: {
    /** 页面可见性（App.vue 用 v-show 常驻挂载）—— 变可见时要 resize 图表，否则宽度算成 0 */
    visible: { type: Boolean, default: true },
  },
  data() {
    return {
      // 选择
      sample: 'top500',
      win: 250,
      sort: 'dev_asc',
      bucket: 'all',
      includeSt: false,
      page: 1,
      pageSize: 50,
      // 数据
      rows: [],
      total: 0,
      pages: 1,
      stats: { count: 0, stCount: 0, groups: {}, dist: [] },
      poolCounts: {},
      windows: [60, 120, 250],
      groups: [],
      bucketLabels: [],
      backtest: { excess: { '250h20': [], '120h20': [], '250h60': [] }, stableYears: [], sweet: [], report: '' },
      reportDate: '2026-10-11',
      backtestCaliber: '',
      // 元信息
      ready: true,
      readyReason: '',
      maAsOf: '',
      priceAsOf: '',
      priceSrc: '',
      priceSrcCounts: {},
      lagTradingDays: 0,
      maFresh: true,
      freshHint: '',
      excessKey: null,
      excessNote: '',
      excessMeasured: true,
      // UI
      loading: false,
      exporting: false,
      error: '',
      tableHeight: 520,
      chart: null,
      summaryOpen: [], // 汇总默认折叠；展开时为 ['summary']
      dlgBacktest: false,
      dlgCaliber: false,
    }
  },
  computed: {
    winShort() {
      return WIN_SHORT
    },
    sampleLabel() {
      return POOL_LABELS[this.sample] || this.sample
    },
    priceSrcLabel() {
      return { realtime: '实时报价（分钟级）', daily: '日K最新收盘', build: '建表时快照' }[this.priceSrc] || this.priceSrc || '—'
    },
    /**
     * ⭐ 现价来源必须**逐只**写清楚：库里的报价表常是「一半新一半旧」
     *   （本机整表停在 09-10；线上盘中新、隔夜残留）。只写一个来源标签会把这件事盖住。
     */
    priceSrcSummary() {
      const c = this.priceSrcCounts || {}
      const parts = []
      if (c.realtime) parts.push(`实时报价 ${c.realtime}`)
      if (c.daily) parts.push(`日K收盘 ${c.daily}`)
      if (c.build) parts.push(`建表快照 ${c.build}`)
      const when = this.priceAsOf ? ` · ${this.priceAsOf}` : ''
      return parts.length ? parts.join(' / ') + when : this.priceSrcLabel + when
    },
    reportHref() {
      return this.backtest.report || ''
    },
    poolOptions() {
      return ['all', 'top500', 'p60', 'p75'].map((value) => {
        const n = this.poolCounts[value]
        return {
          value,
          label: n != null && n !== '' ? `${POOL_LABELS[value]} ${n}` : POOL_LABELS[value],
        }
      })
    },
  },
  watch: {
    visible(v) {
      if (v) this.$nextTick(() => this.resizeChart())
    },
    // 折叠展开后容器才有尺寸；折叠态里 init/setOption 会拿到 0 宽高
    summaryOpen(v) {
      if (Array.isArray(v) && v.includes('summary')) {
        this.$nextTick(() => {
          this.renderChart()
          this.resizeChart()
        })
      }
    },
  },
  async created() {
    await this.loadMeta()
    await this.loadData()
  },
  mounted() {
    this.updateTableHeight()
    window.addEventListener('resize', this.onResize, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    eastmoneyCodeLink,
    stockstarCodeLink,

    /* ---------------- 工具 ---------------- */
    num(v) {
      if (v == null || v === '') return '—'
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
    pct(v) {
      if (v == null || v === '' || !Number.isFinite(Number(v))) return '—'
      const n = Number(v)
      return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`
    },
    sharePct(v) {
      if (v == null || !Number.isFinite(Number(v))) return '—'
      return `${(Number(v) * 100).toFixed(1)}%`
    },
    /** 数值着色：正=红、负=绿（A 股口径）；0 与 null 走中性 */
    clsOf(v) {
      if (v == null || !Number.isFinite(Number(v)) || Number(v) === 0) return ''
      return Number(v) > 0 ? 'up' : 'down'
    },
    rowIndex(i) {
      return (this.page - 1) * this.pageSize + i + 1
    },
    rowClass({ row }) {
      return row.isSt ? 'st-row' : ''
    },
    groupLabel(key) {
      const g = this.groups.find((x) => x.key === key)
      return g ? g.label : key
    },
    groupRange(key) {
      return {
        deep: '≤ −35%', drop: '−35% ~ −15%', near: '−15% ~ +15%', far: '+15% ~ +50%', chase: '> +50%',
      }[key] || ''
    },
    bucketIdxOf(key) {
      // 13 档索引里「均线附近」= −15%~−8% / −8%~−3% / −3%~+3% / +3%~+8% / +8%~+15% ⇒ 取中间 −3%~+3% 作代表
      return { deep: 1, drop: 3, near: 6, far: 9, chase: 12 }[key]
    },
    excessOf(i) {
      const arr = this.backtest.excess[this.excessKey]
      return arr && i != null ? arr[i] : null
    },
    setBucket(key) {
      if (this.bucket === key) return
      this.bucket = key
      this.page = 1
      this.loadData()
    },
    onFilterChange() {
      this.page = 1
      this.loadData()
    },
    onWindowChange() {
      this.page = 1
      this.loadData()
    },

    /* ---------------- 数据 ---------------- */
    buildQuery(overrides = {}) {
      const p = Object.assign({
        sample: this.sample,
        window: this.win,
        bucket: this.bucket,
        sort: this.sort,
        includeSt: this.includeSt ? '1' : '0',
        page: this.page,
        pageSize: this.pageSize,
      }, overrides)
      return Object.entries(p).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')
    },

    applyMeta(data) {
      this.ready = data.ready !== false
      this.readyReason = data.reason || ''
      this.maAsOf = data.maAsOf || ''
      this.priceAsOf = data.priceAsOf || ''
      this.priceSrc = data.priceSrc || ''
      if (data.priceSrcCounts) this.priceSrcCounts = data.priceSrcCounts
      this.lagTradingDays = data.lagTradingDays || 0
      this.maFresh = data.maFresh !== false
      this.freshHint = data.freshHint || ''
      if (Array.isArray(data.windows) && data.windows.length) this.windows = data.windows
      if (Array.isArray(data.groups)) this.groups = data.groups
      if (Array.isArray(data.bucketLabels)) this.bucketLabels = data.bucketLabels
      if (data.backtest) this.backtest = data.backtest
    },

    async loadMeta() {
      try {
        const { res, data } = await fetchWithCredentials('/api/ma-deviation/meta')
        if (!res.ok) throw new Error(data.error || `接口失败 ${res.status}`)
        this.applyMeta(data)
      } catch (e) {
        this.ready = false
        this.readyReason = e.message || String(e)
      }
    },

    async loadData() {
      this.loading = true
      this.error = ''
      try {
        const { res, data } = await fetchWithCredentials(`/api/ma-deviation?${this.buildQuery()}`)
        if (!res.ok) throw new Error(data.error || `年线偏离接口失败 ${res.status}`)
        this.applyMeta(data)
        this.rows = data.list || []
        this.total = data.total || 0
        this.pages = data.pages || 1
        this.stats = data.stats || this.stats
        this.poolCounts = data.poolCounts || this.poolCounts
        this.excessKey = data.excessKey || null
        this.excessNote = data.excessNote || ''
        this.excessMeasured = data.excessMeasured !== false
        this.$nextTick(() => {
          this.updateTableHeight()
          this.renderChart()
        })
      } catch (e) {
        this.rows = []
        this.error = e.message || String(e)
      } finally {
        this.loading = false
      }
    },

    /** 导出：按页拉全量（服务端分页，跨境带宽下不宜一次下发 5,000 行） */
    async exportAll() {
      if (!this.total) {
        this.$message.warning('当前没有可导出的数据')
        return
      }
      this.exporting = true
      const PAGE = 500
      const all = []
      try {
        const pages = Math.max(1, Math.ceil(this.total / PAGE))
        for (let p = 1; p <= pages; p += 1) {
          // eslint-disable-next-line no-await-in-loop
          const { res, data } = await fetchWithCredentials(
            `/api/ma-deviation?${this.buildQuery({ page: p, pageSize: PAGE })}`
          )
          if (!res.ok) throw new Error(data.error || `导出失败（第 ${p} 页 HTTP ${res.status}）`)
          all.push(...(data.list || []))
        }
        const headers = [
          '代码', '名称', '行业', 'ST', '现价', 'MA60', 'MA120', 'MA250',
          '偏离60(%)', '偏离120(%)', '偏离250(%)', '分档', `该档实测超额(${this.excessKey || '未测'})`, '分年为正',
        ]
        const data = all.map((r) => ({
          代码: r.code,
          名称: r.name,
          行业: r.industry || '',
          ST: r.isSt ? 'ST' : '',
          现价: r.price,
          MA60: r.ma60,
          MA120: r.ma120,
          MA250: r.ma250,
          '偏离60(%)': r.dev60,
          '偏离120(%)': r.dev120,
          '偏离250(%)': r.dev250,
          分档: r.bucketLabel,
          [`该档实测超额(${this.excessKey || '未测'})`]: r.excess == null ? '' : r.excess,
          分年为正: r.stableYears == null ? '' : `${r.stableYears}/5`,
        }))
        const sheet = XLSX.utils.json_to_sheet(data, { header: headers })
        sheet['!cols'] = headers.map((h) => ({ wch: Math.max(8, h.length + 2) }))
        const book = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(book, sheet, '年线偏离')
        XLSX.writeFile(book, `年线偏离_${this.sampleLabel}_${this.winShort[this.win].replace(/\s/g, '')}_${this.maAsOf}_${all.length}只.xlsx`)
        this.$message.success(`已导出 ${all.length} 只`)
      } catch (e) {
        this.$message.error(e.message || String(e))
      } finally {
        this.exporting = false
      }
    },

    /* ---------------- 图表 ---------------- */
    renderChart() {
      const el = this.$refs.distChart
      if (!el) return
      if (!this.chart) this.chart = echarts.init(el)
      const labels = this.bucketLabels || []
      const dist = (this.stats && this.stats.dist) || []
      const total = (this.stats && this.stats.count) || 1
      const share = labels.map((_, i) => Number((((dist[i] || 0) / total) * 100).toFixed(2)))
      const exKey = this.excessKey
      const ex = exKey ? (this.backtest.excess[exKey] || []) : null
      const exColor = ex ? ex.map((v) => (v >= 0 ? RED : GREEN)) : []

      const series = [{
        name: '占池%',
        type: 'bar',
        data: share,
        barMaxWidth: 30,
        itemStyle: { color: '#c6e2ff', borderColor: '#a3d0ff', borderWidth: 1, borderRadius: [2, 2, 0, 0] },
      }]
      if (ex) {
        series.push({
          name: '实测超额',
          type: 'line',
          yAxisIndex: 1,
          data: labels.map((_, i) => (ex[i] == null ? null : ex[i])),
          symbol: 'circle',
          symbolSize: 7,
          lineStyle: { color: '#909399', width: 1, type: 'dashed' },
          itemStyle: { color: (p) => exColor[p.dataIndex] },
          label: {
            show: true,
            position: 'top',
            fontSize: 10,
            color: '#606266',
            formatter: (p) => (p.value == null ? '' : Number(p.value).toFixed(2)),
          },
        })
      }

      this.chart.setOption({
        grid: { left: 56, right: 62, top: 24, bottom: 62 },
        tooltip: {
          trigger: 'axis',
          formatter: (ps) => {
            const i = ps[0].dataIndex
            let s = `${labels[i]}<br>该档家数：${dist[i] || 0}（占池 ${share[i]}%）`
            if (ex) s += `<br>该档实测超额：<b>${this.pct(ex[i])}</b>`
            else s += '<br>季线 MA60 未做回测 ⇒ 无实测超额'
            return s
          },
        },
        xAxis: {
          type: 'category',
          data: labels,
          axisLabel: { fontSize: 10.5, color: '#909399', interval: 0, rotate: 45 },
          axisLine: { lineStyle: { color: '#e4e7ed' } },
          axisTick: { show: false },
        },
        yAxis: [
          {
            type: 'value',
            name: '占池 %',
            nameTextStyle: { fontSize: 11, color: '#909399' },
            axisLabel: { fontSize: 11, color: '#909399' },
            splitLine: { lineStyle: { color: '#f2f6fc' } },
          },
          {
            type: 'value',
            name: '超额 %',
            nameTextStyle: { fontSize: 11, color: '#909399' },
            axisLabel: { fontSize: 11, color: '#909399', formatter: '{value}%' },
            splitLine: { show: false },
            show: !!ex,
          },
        ],
        series,
      }, true)
      this.chart.resize()
    },
    resizeChart() {
      if (this.chart) this.chart.resize()
    },

    /* ---------------- 尺寸 ---------------- */
    onResize() {
      this.updateTableHeight()
      this.resizeChart()
    },
    updateTableHeight() {
      this.$nextTick(() => {
        const el = this.$refs.tbl && this.$refs.tbl.$el
        if (!el) {
          this.tableHeight = 520
          return
        }
        const top = el.getBoundingClientRect().top
        const h = Math.max(300, Math.floor(window.innerHeight - top - 46))
        if (h !== this.tableHeight) {
          this.tableHeight = h
          this.$nextTick(() => {
            if (this.$refs.tbl && this.$refs.tbl.doLayout) this.$refs.tbl.doLayout()
          })
        }
      })
    },
  },
}
</script>

<style scoped>
.ma-deviation {
  width: 100%;
  min-width: 0;
}

/* ---------------- 工具栏 ---------------- */
.toolbar {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px 14px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
}

.toolbar + .toolbar {
  margin-top: 10px;
}

.toolbar--sub {
  gap: 10px;
}

.toolbar-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
  white-space: nowrap;
}

.toolbar .spacer {
  flex: 1 1 auto;
}

.notice-trigger,
.doc-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border-radius: 14px;
  font-size: 12px;
  cursor: pointer;
  outline: none;
  font-family: inherit;
}

.notice-trigger {
  border: 1px solid #f5c6cb;
  background: #fef0f0;
  color: #f56c6c;
}

.notice-trigger:hover {
  background: #fde2e2;
  border-color: #f56c6c;
}

.doc-trigger {
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #606266;
}

.doc-trigger:hover {
  border-color: #409eff;
  color: #409eff;
}

.notice-trigger i,
.doc-trigger i {
  font-size: 14px;
}

/* 分档筛选 chip —— 与选中态视觉语言保持一致（浅底 + 同色描边） */
.fchips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.fchip {
  height: 26px;
  padding: 0 11px;
  border: 1px solid #dcdfe6;
  border-radius: 13px;
  background: #fff;
  color: #606266;
  font-size: 12.5px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.fchip .c {
  font-variant-numeric: tabular-nums;
  color: #909399;
}

.fchip:hover {
  border-color: #c6e2ff;
}

.fchip.on {
  background: #ecf5ff;
  border-color: #b3d8ff;
  color: #409EFF;
}

.fchip.on .c {
  color: #409EFF;
}

/* ---------------- 汇总（可折叠，默认收起） ---------------- */
.summary-collapse {
  margin: 12px 0 0;
  border: none;
}

.summary-collapse >>> .el-collapse-item__header {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  background: #fff;
  padding: 0 14px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  height: 40px;
  line-height: 40px;
}

.summary-collapse >>> .el-collapse-item__wrap {
  border: 1px solid #ebeef5;
  border-top: 0;
  border-radius: 0 0 4px 4px;
  background: #fafafa;
}

.summary-collapse >>> .el-collapse-item__content {
  padding: 12px;
}

.summary-collapse >>> .el-collapse-item__arrow {
  margin-right: 4px;
}

.cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
}

.block--in-summary {
  margin-top: 12px;
}

.card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 13px 15px;
  position: relative;
}

.card .l {
  font-size: 12.5px;
  color: #909399;
  margin-bottom: 6px;
}

.card .v {
  font-size: 23px;
  font-weight: 500;
  line-height: 1.2;
}

.card .s {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  line-height: 1.6;
}

/* 警示用「左色条」而不是浅底描边 —— 避免与 fchip 选中态混淆 */
.card.warn::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #f56c6c;
  border-radius: 4px 0 0 4px;
}

.up {
  color: #f56c6c;
}

.down {
  color: #67c23a;
}

.dim {
  color: #909399;
}

.ghost {
  color: #c0c4cc;
  font-weight: 400;
}

.imp {
  color: #c0392b;
  font-weight: 600;
}

/* ---------------- 区块 ---------------- */
.block {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-top: 12px;
}

.block-head {
  padding: 11px 15px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.block-head h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.block-head .hint {
  font-size: 12px;
  color: #909399;
}

.block-body {
  padding: 12px 15px;
}

.legend {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #606266;
  padding: 0 15px 10px;
  flex-wrap: wrap;
  align-items: center;
}

.legend--tags {
  padding-top: 10px;
  padding-bottom: 12px;
}

.legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}

.swatch-bar {
  background: #c6e2ff;
  border: 1px solid #a3d0ff;
}

.swatch-up {
  background: #f56c6c;
}

.swatch-down {
  background: #67c23a;
}

.dist-chart {
  width: 100%;
  height: 320px;
}

.empty-tip {
  padding: 20px;
  text-align: left;
  font-size: 13px;
  color: #909399;
}

/* ---------------- 内联表格（分档实测） ---------------- */
.table-scroll {
  overflow-x: auto;
}

table.grid {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

table.grid th {
  background: #fafafa;
  color: #606266;
  font-weight: 500;
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid #ebeef5;
  white-space: nowrap;
}

table.grid td {
  padding: 7px 10px;
  border-bottom: 1px solid #f2f6fc;
  color: #303133;
  white-space: nowrap;
}

table.grid tr:hover td {
  background: #f5f7fa;
}

table.grid tr.row-hot td {
  background: #fdf6ec;
}

table.grid tr.row-deep td {
  background: #fef0f0;
}

table.grid .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* ---------------- 标签 ---------------- */
.tag {
  display: inline-block;
  font-size: 11.5px;
  line-height: 18px;
  padding: 0 6px;
  border-radius: 3px;
  border: 1px solid;
  white-space: nowrap;
}

.tag.deep {
  color: #f56c6c;
  border-color: #f7b9b9;
  background: #fef0f0;
}

.tag.drop {
  color: #e6a23c;
  border-color: #f5dab1;
  background: #fdf6ec;
}

.tag.near {
  color: #909399;
  border-color: #e4e7ed;
  background: #fafafa;
}

.tag.far {
  color: #67c23a;
  border-color: #b9e2b9;
  background: #f0f9eb;
}

.tag.chase {
  color: #fff;
  background: #67c23a;
  border-color: #67c23a;
}

.tag.st {
  color: #f56c6c;
  border-color: #f7b9b9;
  background: #fef0f0;
  font-size: 10.5px;
  line-height: 15px;
  padding: 0 4px;
  margin-left: 4px;
}

.tag.yrs {
  color: #67c23a;
  border-color: #b9e2b9;
  background: #f0f9eb;
  font-size: 10.5px;
  line-height: 15px;
  padding: 0 4px;
  margin-left: 4px;
}

/* ---------------- 键值表 ---------------- */
.kv {
  display: grid;
  grid-template-columns: 104px 1fr;
  gap: 6px 12px;
  font-size: 12.5px;
  text-align: left;
  margin: 0;
}

.kv dt {
  color: #909399;
}

.kv dd {
  margin: 0;
  color: #303133;
  line-height: 1.75;
}

.fnote {
  font-size: 12.5px;
  color: #606266;
  line-height: 1.8;
  text-align: left;
  margin-bottom: 8px;
}

code {
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 3px;
  padding: 1px 5px;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.tip {
  color: #c0c4cc;
  cursor: help;
}

.tip:hover {
  color: #909399;
}

/* ---------------- 表格微调 ---------------- */
.ma-deviation /deep/ .el-table th > .cell {
  white-space: nowrap;
  line-height: 22px;
}

.ma-deviation /deep/ .el-table .col-active {
  background: #f5f9ff;
}

.ma-deviation /deep/ .el-table .num {
  font-variant-numeric: tabular-nums;
}

.ma-deviation /deep/ .el-table tr.st-row td {
  background: #fffbf5;
}

.ma-deviation /deep/ .el-table {
  font-size: 12.5px;
}

@media (max-width: 1280px) {
  .cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>

<!-- popover / dialog 挂到 body，需非 scoped -->
<style>
.mad-doc-dialog {
  text-align: left;
}

.mad-doc-dialog .el-dialog__body {
  padding: 12px 20px 20px;
  max-height: min(70vh, 640px);
  overflow-y: auto;
}

.mad-notice-popper {
  padding: 12px 14px !important;
}

.mad-notice-pop .mad-notice-title {
  font-size: 13px;
  font-weight: 600;
  color: #f56c6c;
  margin-bottom: 8px;
}

.mad-notice-pop p {
  margin: 0 0 8px;
  font-size: 12.5px;
  line-height: 1.7;
  color: #606266;
}

.mad-notice-pop p:last-child {
  margin-bottom: 0;
}

.mad-notice-pop .up {
  color: #f56c6c;
  font-weight: 600;
}

.mad-notice-pop .down {
  color: #67c23a;
  font-weight: 600;
}

.mad-notice-pop .imp {
  color: #c0392b;
  font-weight: 600;
}

.mad-notice-pop code {
  background: #f5f7fa;
  padding: 0 3px;
  border-radius: 2px;
  color: #606266;
  font-size: 12px;
}
</style>
